import { ArrowDown, Button } from '@repo/atomic-components';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useResizeObserver } from '@/hooks';

type ScrollToRefButtonProps = {
  refElem: RefObject<HTMLElement | null>;
  scrollContainerRef: RefObject<HTMLElement | null>;
  messagesLength: number;
  onResumeScroll?: () => void;
};

export const ScrollToRefButton = ({
  refElem,
  scrollContainerRef,
  messagesLength,
  onResumeScroll,
}: ScrollToRefButtonProps) => {
  const [isChatScrollVisible, setIsChatScrollVisible] = useState<boolean>(false);
  const [isAutoHidden, setIsAutoHidden] = useState<boolean>(false);
  const ignoreScrollRevealUntilRef = useRef(0);
  const autoHideTimerRef = useRef<number | null>(null);
  const [scrollContainerEl, setScrollContainerEl] = useState<HTMLElement | null>(null);
  const [scrollContainerInnerEl, setScrollContainerInnerEl] = useState<Element | null>(null);
  const [distanceFromBottom, setDistanceFromBottom] = useState<number>(0);

  const restartAutoHideTimer = useCallback(() => {
    if (autoHideTimerRef.current != null) {
      window.clearTimeout(autoHideTimerRef.current);
    }
    autoHideTimerRef.current = window.setTimeout(() => {
      setIsAutoHidden(true);
    }, 5000);
  }, []);

  const checkScrollVisibility = useCallback(() => {
    if (!scrollContainerEl) return;

    requestAnimationFrame(() => {
      const { scrollHeight, clientHeight, scrollTop } = scrollContainerEl;
      setIsChatScrollVisible(scrollHeight > clientHeight);
      setDistanceFromBottom(scrollHeight - clientHeight - scrollTop);
    });
  }, [scrollContainerEl]);

  useEffect(() => {
    if (!scrollContainerEl) return;

    const onScroll = () => {
      // Any user scroll interaction should make the button available again.
      // When we programmatically scroll-to-bottom, we suppress this for a moment.
      if (Date.now() >= ignoreScrollRevealUntilRef.current) {
        setIsAutoHidden(false);
        restartAutoHideTimer();
      }
      checkScrollVisibility();
    };

    scrollContainerEl.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollContainerEl.removeEventListener('scroll', onScroll);
    };
  }, [scrollContainerEl, checkScrollVisibility, restartAutoHideTimer]);

  useResizeObserver(scrollContainerEl, undefined, checkScrollVisibility);
  useResizeObserver(scrollContainerInnerEl, undefined, checkScrollVisibility);

  const handleScrollToBottom = () => {
    onResumeScroll?.();
    setIsAutoHidden(true);
    ignoreScrollRevealUntilRef.current = Date.now() + 800;
    refElem?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Sync refs to state for useResizeObserver without breaking the react-hooks/refs rule
  useEffect(() => {
    if (scrollContainerRef.current) {
      setScrollContainerEl(scrollContainerRef.current);
      setScrollContainerInnerEl(scrollContainerRef.current.firstElementChild || null);
    }
  }, [scrollContainerRef, messagesLength]);

  useEffect(() => {
    checkScrollVisibility();
  }, [messagesLength, checkScrollVisibility]);

  // Show only when user is meaningfully away from bottom.
  // This avoids flicker from layout shifts when the input expands.
  const SHOW_AFTER_PX = 200;
  const shouldShow = isChatScrollVisible && distanceFromBottom >= SHOW_AFTER_PX && !isAutoHidden;

  useEffect(() => {
    // Manage only timers here (no setState-in-effect).
    if (!isChatScrollVisible) {
      if (autoHideTimerRef.current != null) {
        window.clearTimeout(autoHideTimerRef.current);
        autoHideTimerRef.current = null;
      }
      return;
    }
  }, [isChatScrollVisible]);

  useEffect(() => {
    if (!shouldShow) return;
    restartAutoHideTimer();
  }, [shouldShow, restartAutoHideTimer]);

  if (!shouldShow) {
    return null;
  }

  return (
    <Button
      type="button"
      outlined
      onClick={handleScrollToBottom}
      className="bg-theme-white border-theme-border-grey hover:bg-theme-background-grey! text-theme-black pointer-events-auto flex !h-[32px] !w-[32px] items-center justify-center p-0"
    >
      <ArrowDown />
    </Button>
  );
};
